import React from 'react';
import GuideHero from 'components/guide-hero';
import {
  CitationBlock,
  CitationBlockMini,
  CitationExampleBox,
  CitationExampleInList,
  GuideHeader,
  GuideSection,
  GuideTitle,
  GuideWrapper,
  TextWithColor,
} from 'components/guide';
import GuideLinkButton, {
  GuideLinkButtonSection,
} from 'components/guide-link-button';
import Footer from 'components/footer';
import Link from 'next/link';

const ChicagoGuide: React.FC = () => (
  <>
    <GuideHero
      header="Our Complete Guide to Citing in Chicago 17 Format"
      imgSrc={require('images/Chicago-guide-hero.png')}
      imgAlt="Laptop with screenshot on screen of manual for Chicago format"
    >
      <p>
        Welcome to our Chicago Style guide, where footnotes reign supreme and
        bibliographies shine brighter than the skyline of the Windy City itself!
        This style is often used in history, arts, and social sciences, offering
        a robust and versatile method for crediting your sources.
      </p>
      <p>
        It&apos;s time to trade your deep-dish pizza for a deep dive into the
        realm of the Chicago Manual of Style &mdash; brace yourself to become an
        expert in one of academia&apos;s most exciting citation styles.
      </p>
      <p>Let&apos;s get started, shall we?</p>
    </GuideHero>
    <GuideWrapper>
      <GuideTitle>Discover More Bibcitation Guides</GuideTitle>
      <GuideLinkButtonSection>
        <GuideLinkButton text="MLA Guide" href="/mla" variant="purple" />
        <GuideLinkButton text="IEEE Guide" href="/ieee" variant="blue" />
        <GuideLinkButton text="APA Guide" href="/apa" variant="orange" />
      </GuideLinkButtonSection>

      <Overview />
      <FormatFootnotes />
      <FullShortenedFootnotes />
      <FormatBibliography />
      <CiteBooks />
      <CiteJournalArticles />
      <CiteWebsites />
      <CiteBookChapters />
      <CiteNewspaperMagazine />
      <CiteConferencePaper />
      <CiteAudioVisual />
      <CiteSocialMedia />
      <CiteImagesArtwork />
      <CiteThesesDissertations />
      <CiteLegalDocuments />

      <CiteClassicalWorks />
      <CiteCourseMaterials />
      <CitePersonalComms />
      <FinalNotes />
    </GuideWrapper>
    <Footer />
  </>
);

const Overview: React.FC = () => (
  <GuideSection id="Overview">
    <GuideHeader>Chicago Style Overview</GuideHeader>
    <p>
      The Chicago Manual of Style, often abbreviated as CMS, is a highly
      respected style guide within academic and publishing circles. Its
      comprehensive approach covers a broad range of topics, making it an ideal
      tool for authors, editors, proofreaders, indexers, and more. Primarily
      used within the fields of history and the humanities, the Chicago Style is
      known for its flexibility, offering two distinct citation methods: the
      Notes-Bibliography System (NB) and the Author-Date System.
    </p>
    <p>
      The NB system is generally preferred for humanities papers that require
      broad source material, while the Author-Date system is more common in the
      physical, natural, and social sciences.
    </p>
    <p>
      <strong>In the Notes-Bibliography system</strong>, you cite sources in
      numbered footnotes or endnotes. Each note corresponds to a superscripted
      note number in the text. This system allows for a wide range of sources,
      and flexibility in terms of the types of information you can include.
    </p>
    <p>
      <strong>The Author-Date system</strong>, on the other hand, involves
      in-text citations, featuring the author&apos;s last name, the year of
      publication, and page numbers if relevant. This is followed by a reference
      list, similar to the one in APA style.
    </p>
    <p>
      The choice between the two systems often depends on the subject matter and
      the nature of sources cited, as both are equally recognized within the
      Chicago style.
    </p>
  </GuideSection>
);

const FormatFootnotes: React.FC = () => (
  <GuideSection id="Footnotes">
    <GuideHeader>Format Chicago Style Footnotes</GuideHeader>
    <p>
      <strong>What are footnotes?</strong>
    </p>
    <p>
      Footnotes are notes placed at the bottom of a page in a document that
      comment on or cite a reference for a designated part of the text above it.
      In the Chicago Style, footnotes are used to cite sources and provide
      additional information.
    </p>
    <p>
      <strong>Where should footnotes be placed?</strong>
    </p>
    <p>
      Footnotes should be located at the bottom of the page where the reference
      occurs. They are typically separated from the main body of text by a line,
      beginning at the left margin of the paper.
    </p>
    <p>
      Each footnote is indented and numbered consecutively throughout a piece of
      writing. The number appears after the relevant punctuation mark &mdash;
      for instance, at the end of a sentence following the period. If the
      reference applies to a sentence or a part of it, the footnote should come
      after the comma or the final punctuation.
    </p>
    <p>
      <strong>How to insert footnotes?</strong>
    </p>
    <p>
      Most word processing software like Microsoft Word and Google Docs have
      built-in features to add footnotes. In Word, you can add a footnote by
      clicking on the &quot;References&quot; tab and then selecting &quot;Insert
      Footnote.&quot; In Google Docs, click on &quot;Insert&quot; then
      &quot;Footnote.&quot;
    </p>
    <p>
      <strong>How to format footnotes in the Chicago Style?</strong>
    </p>
    <p>
      After inserting the footnote, you should write the citation. The first
      line of each footnote is indented five spaces, and the line is
      single-spaced with one line space between each note. The note number is
      followed by a period and a space.
    </p>
    <p>
      Footnote numbers should be superscripted in the text, but the numbers in
      the footnote section at the bottom of the page should be full-sized,
      followed by a period.
    </p>
    <p>
      Subsequent references to the same text can be shortened to the
      author&apos;s last name, the title (shortened if longer than four words),
      and the page number(s).
    </p>
    <p>
      <strong>Consistency is key!</strong>
    </p>
    <p>
      It&apos;s important to remain consistent in your use of footnotes
      throughout your document. Always use the same style and follow the same
      rules for every footnote.
    </p>
  </GuideSection>
);

const FullShortenedFootnotes: React.FC = () => (
  <GuideSection id="FullShortenedFootnotes">
    <GuideHeader>Full or Shortened Footnote?</GuideHeader>
    <p>
      Footnotes in Chicago style can be presented in two forms: full and
      shortened. These two formats are used depending on whether a source is
      cited for the first time or in subsequent citations.
    </p>

    <p>
      <strong>Full Footnotes</strong>
    </p>
    <p>
      Full footnotes are used when a source is cited for the first time in a
      text. They include complete bibliographic information, such as the
      author&apos;s name, the title of the source, the publication date, and any
      other relevant details.
    </p>
    <p>
      For example, the format for a full footnote for a book citation would be
      as follows:
    </p>
    <CitationBlockMini>
      1. Author&apos;s First Name Last Name, <em>Title of the Book</em> (Place
      of Publication: Publisher, Year), page number(s).
    </CitationBlockMini>
    <p>
      <strong>Example:</strong>
    </p>

    <CitationExampleBox>
      2. Jane Smith, <em>History of Architecture</em> (New York: Penguin Books,
      2020), 46.
    </CitationExampleBox>
    <br />
    <p>
      <strong>Shortened Footnotes</strong>
    </p>

    <p>
      Shortened footnotes are used for subsequent citations of a source already
      cited in a full footnote. They typically include only the author&apos;s
      last name, a shortened title (if the title is long), and the page
      number(s).
    </p>
    <p>
      Shortened footnotes help maintain readability while still providing
      necessary information for readers to locate the original source.
    </p>
    <p>
      <strong>A shortened footnote for a book citation:</strong>
    </p>
    <CitationExampleBox>
      Smith, <em>History of Architecture,</em> 78.
    </CitationExampleBox>
    <br />
    <p>
      <strong>Tips for Using Full and Shortened Footnotes</strong>
    </p>
    <p>
      To effectively use full and shortened footnotes in Chicago style, keep the
      following tips in mind:
    </p>
    <ul>
      <li>
        Use a full footnote for the first citation of a source and shortened
        footnotes for subsequent citations.
      </li>
      <li>
        Ensure that your footnotes are numbered consecutively throughout your
        document.
      </li>
      <li>
        Use a consistent format for all full and shortened footnotes, adhering
        to the guidelines provided by the Chicago Manual of Style.
      </li>
      <li>
        Include a bibliography at the end of your document to provide a complete
        list of sources cited, further facilitating research for your readers.
      </li>
    </ul>
    <p>
      Below you will find an example of how these two types of Chicago footnotes
      would look like in an actual paper:
    </p>
    <img
      src={require('images/Chicago-screenshot-1.png')}
      alt="Screenshot of a Chicago reference page"
    />
  </GuideSection>
);

const FormatBibliography: React.FC = () => (
  <GuideSection id="FormatBibliography">
    <GuideHeader>Format Your Bibliography in Chicago Style</GuideHeader>
    <p>
      A bibliography is an essential part of any research paper, allowing
      readers to easily locate the sources you&apos;ve cited. In the Chicago
      style, the bibliography is placed at the end of your work and provides
      full information about each source.
    </p>
    <ul>
      <li>
        <strong>Placement:</strong> The bibliography should appear at the end of
        your paper, on its own new page(s). The word &quot;Bibliography&quot;
        should be centered at the top of the page.
      </li>
      <li>
        <strong>Order:</strong> Entries in the bibliography should be listed in
        alphabetical order by the last name of the first author. If there&apos;s
        no author, use the title of the work.
      </li>
      <li>
        <strong>Indentation:</strong> Use a hanging indent for each entry. This
        means the first line is aligned with the left margin, and any subsequent
        lines are indented by 0.5 inches.
      </li>
      <li>
        <strong>Line Spacing:</strong> Entries should be single-spaced, but
        there should be one blank line space between each entry.
      </li>
      <li>
        <strong>Capitalization:</strong> In titles and subtitles, capitalize the
        first word, the last word, and all principal words. This includes words
        that follow hyphens in compound terms.
      </li>
      <li>
        <strong>Punctuation:</strong> In a bibliography, all major elements are
        separated by periods.
      </li>
      <li>
        <strong>Titles:</strong> Book and periodical titles should be
        italicized. If the work is an article or a chapter from a larger work,
        the title should be in quotation marks, and the book or periodical title
        that follows should be italicized.
      </li>
    </ul>
    <img
      src={require('images/Chicago-screenshot-2.png')}
      alt="Screenshot of a Chicago reference page"
    />
    <p>
      The exact format may vary depending on the source type (e.g., book with
      multiple authors, edited volumes, online sources, etc.). Always consult
      the Chicago Manual of Style or your professor/assignment guidelines for
      any specific formatting rules.
    </p>
  </GuideSection>
);

const CiteBooks: React.FC = () => (
  <GuideSection id="CiteBooks">
    <GuideHeader>Cite Books in Chicago Style</GuideHeader>
    <p>
      The Chicago Style provides two primary citation styles: the Notes and
      Bibliography system, used mainly in the humanities, and the Author-Date
      system, typically used in the sciences. The format for citing books varies
      depending on the system used.
    </p>
    <p>
      In the Notes and Bibliography system, the first note for each source
      provides full bibliographic information. For a book, this includes the
      author&apos;s name, the book&apos;s title (in italics), the city of
      publication, the publisher&apos;s name, and the year of publication.
    </p>
    <p>
      Subsequent notes for the same source can be shortened to the author&apos;s
      last name, a shortened version of the title, and the page number. In the
      bibliography, the author&apos;s name is inverted (last name first), and
      the elements of the citation are separated by periods instead of commas.
    </p>
    <p>
      Here&apos;s the general format for a book citation in{' '}
      <TextWithColor>Notes and Bibliography:</TextWithColor>
    </p>
    <p>
      <strong>First note:</strong>
    </p>
    <CitationBlockMini>
      Author&apos;s First Name Last Name, <em>Title of Book</em> (Place of
      Publication: Publisher, Year of Publication), page number.
    </CitationBlockMini>
    <p>
      <strong>Chicago full note citation for a book:</strong>
    </p>
    <CitationExampleBox>
      Karl Polanyi, <em>The Great Transformation</em> (New York: Farrar &amp;
      Rineaus, 1944), 56.
    </CitationExampleBox>
    <p>
      <strong>Subsequent notes:</strong>
    </p>
    <CitationBlockMini>
      Author&apos;s Last Name, <em>Shortened Title,</em> page number.
    </CitationBlockMini>
    <p>
      <strong>Example:</strong>
    </p>
    <CitationExampleBox>
      Polanyi, <em>Transformation</em>, 78.
    </CitationExampleBox>
    <p>
      Below you will see the standard structure for a bibliography entry for a
      book citation in the Chicago{' '}
      <TextWithColor>Notes and Bibliography</TextWithColor> style:
    </p>
    <CitationBlock header="Citing a book in Chicago">
      Author&apos;s Last Name, First Name. <em>Title of Book.</em> Edition (if
      any). Place of Publication: Publisher, Year of Publication.
    </CitationBlock>
    <p>
      <strong>For Example:</strong>
    </p>
    <CitationExampleBox>
      Polanyi, Karl. <em>The Great Transformation.</em> New York: Farrar &
      Rineaus, 1944.
    </CitationExampleBox>
    <p>
      In the Author-Date system, a brief parenthetical citation, including the
      author&apos;s last name, the year of publication, and a page number,
      appears in the text. Full bibliographic details are provided in a
      reference list at the end of the document, arranged alphabetically by
      author&apos;s last name.
    </p>
    <p>
      Here&apos;s the general format for a book citation in{' '}
      <TextWithColor>Author-Date:</TextWithColor>
    </p>
    <p>
      <strong>In-text citation:</strong>
    </p>
    <CitationBlockMini>
      (Author&apos;s Last Name Year of Publication, page number)
    </CitationBlockMini>
    <p>
      <strong>Example:</strong>
    </p>
    <CitationExampleBox>(Polanyi 1944, 56)</CitationExampleBox>
    <p>
      <strong>Reference list:</strong>
    </p>
    <CitationBlockMini>
      Author&apos;s Last Name, First Name. Year of Publication.{' '}
      <em>Title of Book. </em>
      Place of Publication: Publisher.
    </CitationBlockMini>
    <p>
      <strong>Example:</strong>
    </p>
    <CitationExampleBox>
      Polanyi, Karl. 1944. <em>The Great Transformation.</em> New York: Farrar
      &amp; Rineaus.
    </CitationExampleBox>
    <p>
      For books with multiple authors, editors, editions, volumes, or translated
      versions, the citation format can become more complex. Always consult the
      CMOS or a trusted citation guide for more specific instructions.
    </p>
  </GuideSection>
);

const CiteJournalArticles: React.FC = () => (
  <GuideSection id="CiteJournalArticles">
    <GuideHeader>Cite Journal Articles in Chicago Style</GuideHeader>
    <p>
      Like with book citations, Chicago style provides two primary citation
      styles for journal articles: the Notes and Bibliography system and the
      Author-Date system.
    </p>

    <p>
      Here&apos;s the general format for a journal article citation in{' '}
      <TextWithColor>Notes and Bibliography:</TextWithColor>
    </p>
    <p>
      <strong>First note:</strong>
    </p>
    <CitationBlockMini>
      Author&apos;s First Name Last Name, &quot;Title of Article,&quot;{' '}
      <em>Title of Journal</em> Volume Number, no. Issue (Year of Publication):
      page numbers. DOI or URL.
    </CitationBlockMini>
    <p>
      <strong>Chicago full note citation for a journal article:</strong>
    </p>
    <CitationExampleBox>
      John Maynard Keynes, &quot;The Economic Consequences of Peace,&quot;
      <em> Journal of Economic Literature</em> 12, no. 6 (1919): 457&ndash;473.
      https://doi.org/10.65/086jgd.
    </CitationExampleBox>
    <p>
      <strong>Subsequent notes:</strong>
    </p>
    <CitationBlockMini>
      Author&apos;s Last Name, &quot;Shortened Title,&quot; page numbers.
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Keynes, &quot;Economic Consequences,&quot; 461.
    </CitationExampleBox>
    <p>
      Below you will see the standard structure for a bibliography entry for a
      book citation in the Chicago{' '}
      <TextWithColor>Notes and Bibliography</TextWithColor> style:
    </p>
    <CitationBlock header="Citing a journal article in Chicago">
      Author&apos;s Last Name, First Name. &quot;Title of Article.&quot;{' '}
      <em>Title of Journal</em> volume number, no. Issue number (Year of
      Publication): page numbers. DOI or URL (if available)
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Keynes, John Maynard. &quot;The Economic Consequences of the Peace.&quot;
      <em>Journal of Economic Literature</em> 12, no. 6 (1919): 457&ndash;473.
    </CitationExampleBox>
    <p>
      In the Author-Date system, a brief parenthetical citation, including the
      author&apos;s last name, the year of publication, and a page number,
      appears in the text. Full bibliographic details are provided in a
      reference list at the end of the document, arranged alphabetically by
      author&apos;s last name.
    </p>
    <p>
      Here&apos;s the general format for a journal article citation in
      <TextWithColor> Author-Date:</TextWithColor>
    </p>
    <p>
      <strong>In-text citation for a journal article:</strong>
    </p>
    <CitationBlockMini>
      (Author&apos;s Last Name Year of Publication, page numbers)
    </CitationBlockMini>
    <p>
      <strong>Example:</strong>
    </p>
    <CitationExampleBox>(Keynes 1919, 461&ndash;466)</CitationExampleBox>
    <p>
      <strong>Reference list entry for a journal article:</strong>
    </p>
    <CitationBlockMini>
      Author&apos;s Last Name, First Name. Year of Publication. &quot;Title of
      Article.&quot; <em>Title of Journal</em> Volume number, no. Issue: page
      numbers. DOI or URL (if available)
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Keynes, John Maynard. 1919. &quot;The Economic Consequences of the
      Peace.&quot; <em>Journal of Economic Literature</em> 12, no. 6:
      457&ndash;473.
    </CitationExampleBox>
    <br />
    <p>
      Let&apos;s delve into some more complex scenarios for citing journal
      articles in Chicago.
    </p>
    <p>
      If a journal article has more than one author, list the authors in the
      order they appear in the publication. Both in the notes and the
      bibliography, use &quot;and&quot; to separate the authors&apos; names.
    </p>
    <p>
      <strong>
        First note citation for a journal article with two authors in Notes and
        Bibliography:
      </strong>
    </p>
    <CitationExampleBox>
      <TextWithColor>Alice Johnson and John Keynes</TextWithColor>, &quot;The
      Economic Consequences of the Peace,&quot;{' '}
      <em>Journal of Economic Literature </em>
      12, no. 6 (1919): 457&ndash;473.
    </CitationExampleBox>
    <p>
      <strong>
        Bibliography entry for a journal article with two authors:
      </strong>
    </p>
    <CitationExampleBox>
      <TextWithColor>Johnson, Alice, and John Keynes</TextWithColor>, &quot;The
      Economic Consequences of the Peace.&quot;{' '}
      <em>Journal of Economic Literature</em> 12, no. 6 (1919): 457&ndash;473.
    </CitationExampleBox>
    <p>
      If the article is from an online journal or a database, you should include
      a DOI (Digital Object Identifier) or URL at the end of the citation.
    </p>
    <p>
      <strong>
        A first note citation for a journal article found in an online journal
        or a database:
      </strong>
    </p>
    <CitationExampleBox>
      James Johnson and Susan Thompson, &quot;The Impact of Social Media on
      Teens,&quot; <em>Youth Studies</em> 10, no. 1 (2022): 35&ndash;52,{' '}
      <TextWithColor>https://www.jstor.org/stable/43211221</TextWithColor>.
    </CitationExampleBox>
    <p>
      Remember, these are just a few of the possible variations you might
      encounter when citing journal articles in Chicago style. Always consult
      the CMOS or a reliable citation guide for more specific instructions if
      you encounter unusual citation scenarios.
    </p>
  </GuideSection>
);

const CiteWebsites: React.FC = () => (
  <GuideSection id="CiteWebsites">
    <GuideHeader>Cite Websites in Chicago Style</GuideHeader>
    <p>
      The Chicago Manual of Style provides a comprehensive guide for citing
      various types of sources, including websites. The aim is to provide a
      consistent and clear method for acknowledging sources and avoiding
      plagiarism. With the proliferation of digital content, it&apos;s
      increasingly common to cite websites in academic work.
    </p>
    <p>
      While citing a website in Chicago style generally follows a standard
      format, there are exceptions and different scenarios that can alter this
      format. The main components include the author&apos;s name, the title of
      the web page, the name of the website, the publishing organization, the
      publication or revision date, and the URL.
    </p>
    <p>
      Here&apos;s the general format for a journal article citation in{' '}
      <TextWithColor>Notes and Bibliography</TextWithColor>:
    </p>
    <p>
      <strong>First note citation for a website:</strong>
    </p>
    <CitationBlockMini>
      Author&apos;s First Name Last Name, &quot;Title of Web Page,&quot; Website
      Name, Publication Date (if available), URL.
    </CitationBlockMini>
    <p>
      <strong>Example:</strong>
    </p>
    <CitationExampleBox>
      Antony Jenkins, &quot;South Africa,&quot; Encyclopedia Britannica, July
      26, 1999, www.britannica.com/place/South-Africa/Urban-settlement.
    </CitationExampleBox>
    <p>
      <strong>Subsequent notes:</strong>
    </p>
    <CitationBlockMini>
      Author&apos;s Last Name, &quot;Shortened Title&quot;.
    </CitationBlockMini>
    <p>
      <strong>Short note example:</strong>
    </p>
    <CitationExampleBox>Jenkins, &quot;South Africa.&quot;</CitationExampleBox>
    <p>
      Below you will see the standard structure for a bibliography entry for a
      website citation in the Chicago{' '}
      <TextWithColor>Notes and Bibliography</TextWithColor> style:
    </p>
    <CitationBlock header="Citing a website in Chicago">
      Author&apos;s Last Name, First Name. &quot;Title of Web Page.&quot;
      Publisher or Website Name. Publication Date (if available). URL.
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Lonely Planet Traveller. &quot;Eating in Copenhagen.&quot; BBC, December
      28, 2012.
      www.bbc.com/travel/article/20121205-mini-guide-to-eating-in-copenhagen.
    </CitationExampleBox>
    <p>
      In the Author-Date system, a website is first cited briefly in the text,
      usually in parentheses, by author&apos;s last name and date of
      publication. The short citations are amplified in a list of references,
      where full bibliographic information is provided.
    </p>
    <p>
      Here&apos;s the general format for various website citations in the{' '}
      <TextWithColor>Author-Date</TextWithColor> style:
    </p>
    <p>
      <strong>In-text citation for a website:</strong>
    </p>
    <CitationBlockMini>
      (Author&apos;s Last Name Year of Publication)
    </CitationBlockMini>
    <p>
      <strong>Example:</strong>
    </p>
    <CitationExampleBox>(Piesing 2023)</CitationExampleBox>
    <p>
      <strong>Reference list entry for a website:</strong>
    </p>
    <CitationBlockMini>
      Author&apos;s Last Name, First Name. Year. &quot;Title of Web Page.&quot;
      Publisher or Website Name. Date modified or accessed (if available). URL.
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Piesing, Mark. 2023. &quot;The Crazy Plan to Explode a Nuclear Bomb on the
      Moon.&quot; BBC, Last modified May 17, 2023.
      https://www.bbc.com/future/article/crazy-plan-to-explode-moon.
    </CitationExampleBox>

    <p>
      If there&apos;s no author, start with the title of the webpage. If
      there&apos;s no publication date, use the abbreviation &quot;n.d&quot; and
      then include the access date after the website name.
    </p>
    <p>
      <strong>Some Exceptions and Notes:</strong>
    </p>
    <ul>
      <li>
        If the website name is essentially the same as the author, it can be
        omitted to avoid repetition.
      </li>
      <li>
        If you cannot find a publication date or if the webpage is continuously
        updated, use an access date instead.
      </li>
      <li>
        If the URL is excessively long or the result of a short-term search,
        provide the homepage or a more stable URL, if possible.
      </li>
      <li>
        For blog posts, treat the blog like a periodical, and include the post
        title in quotation marks and the blog name in italics.
      </li>
    </ul>
  </GuideSection>
);

const CiteBookChapters: React.FC = () => (
  <GuideSection id="CiteBookChapters">
    <GuideHeader>Cite Book Chapters in Chicago Style</GuideHeader>
    <p>
      In the vast landscape of academic writing, it&apos;s common to find
      valuable insights in a single chapter of a book rather than the entire
      volume. Whenever this occurs, it&apos;s important to correctly cite the
      specific chapter you&apos;re referencing. The Chicago Manual of Style
      offers comprehensive guidelines for citing book chapters. Both systems
      provided by Chicago style, the notes-bibliography style and the
      author-date style, accommodate chapter-specific citations.
    </p>
    <p>
      Here&apos;s how you can structure book chapter citations in{' '}
      <TextWithColor>Notes and Bibliography</TextWithColor>:
    </p>
    <p>
      <strong>First note:</strong>
    </p>
    <CitationBlockMini>
      Author&apos;s First Name Last Name, &quot;Title of Chapter,&quot; in{' '}
      <em>Title of Book,</em> ed. Editor&apos;s First Name Last Name (Place of
      Publication: Publisher, Year), page number(s).
    </CitationBlockMini>
    <p>
      <strong>Example:</strong>
    </p>
    <CitationExampleBox>
      Rainbow Sparkles, &quot;The Diet of Unicorns,&quot; in{' '}
      <em>Comprehensive Guide to Unicorn Care</em>, ed. Glitter Moonbeam
      (Fairyland: Enchanted Forest Press, 2023), 5&ndash;60.
    </CitationExampleBox>
    <p>
      Below you will see the standard structure for a bibliography entry for a
      book chapter citation in the Chicago{' '}
      <TextWithColor>Notes and Bibliography</TextWithColor> style:
    </p>
    <CitationBlock header="Citing a book chapter in Chicago">
      Author&apos;s Last name, First name. &quot;Title of Chapter.&quot; In
      <em>Title of Book,</em> edited by Editor&apos;s First Name Last Name, page
      numbers. Place of Publication: Publisher, Year.
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Sparkles, Rainbow. &quot;The Diet of Unicorns.&quot; In{' '}
      <em>Comprehensive Guide to Unicorn Care</em>, edited by Glitter Moon,
      5&ndash;60. Fairyland: Enchanted Forest Press, 2023.
    </CitationExampleBox>
    <p>
      In the <TextWithColor>Author-Date style</TextWithColor>, sources are cited
      briefly in the text, usually in parentheses, by author&apos;s last name
      and date of publication. The short citations are amplified in a list of
      references, where full bibliographic information is provided.
    </p>
    <p>
      <strong>In-text citation for a book chapter:</strong>
    </p>
    <CitationBlockMini>
      (Author&apos;s Last Name Year of Publication, page number)
    </CitationBlockMini>
    <p>
      <strong>Example:</strong>
    </p>
    <CitationExampleBox>(Sparkles 2023, 55)</CitationExampleBox>
    <p>
      <strong>Reference list entry for a book chapter:</strong>
    </p>
    <CitationBlockMini>
      Author&apos;s Last name, First name. Year. &quot;Title of Chapter.&quot;
      In <em>Title of Book</em>, edited by Editor&apos;s First name Last name,
      page range. Place of publication: Publisher.
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>

    <CitationExampleBox>
      Sparkles, Rainbow. 2023. &quot;The Diet of Unicorns.&quot; In
      <em> Comprehensive Guide to Unicorn Care</em>, edited by Glitter Moon,
      5&ndash;60. Fairyland: Enchanted Forest Press.
    </CitationExampleBox>

    <br />
    <p>
      However, there are a few special situations you might encounter when
      citing book chapters in Chicago. Here are a few potential exceptions and
      how to handle them:
    </p>

    <p>
      <strong>No author</strong>:
    </p>

    <p>
      If the chapter does not have a specific author, start with the chapter
      title in your citation.
    </p>

    <p>
      <strong>No editor</strong>:
    </p>

    <p>
      If the book doesn&apos;t have an editor, simply omit the &quot;ed.
      Editor&apos;s First name Last name&quot; from your citation.
    </p>

    <p>
      <strong>Chapter in a single-author book</strong>:
    </p>

    <p>
      If you&apos;re citing multiple chapters from the same book, and the book
      is written entirely by one author (not edited), you don&apos;t need to
      cite each chapter individually. Instead, you can cite the entire book.
      But, if you specifically want to emphasize different chapters, it is
      permissible to cite each one separately.
    </p>

    <p>
      <strong>Translated or revised editions</strong>:
    </p>

    <p>
      If you&apos;re citing from a translated or revised edition of a book, you
      should include this information in your citation. For translated books,
      after the title, add &quot;translated by Translator&apos;s First name Last
      name.&quot; For revised editions, after the title, add the edition number
      (e.g., &quot;2nd ed.&quot;).
    </p>
  </GuideSection>
);

const CiteNewspaperMagazine: React.FC = () => (
  <GuideSection id="CiteNewsPaperMagazine">
    <GuideHeader>Cite Newspaper &amp; Magazine Articles in Chicago</GuideHeader>
    <p>
      As with book chapters, the Chicago Manual of Style provides two basic
      citation systems: notes-bibliography style (or simply bibliography style)
      and author-date style (sometimes called reference list style).
    </p>
    <p>
      How to structure newspaper and magazine citations in{' '}
      <TextWithColor>Notes and Bibliography</TextWithColor>:
    </p>
    <p>
      <strong>First note citation for a newspaper or magazine article:</strong>
    </p>
    <CitationBlockMini>
      Author&apos;s First Name Last Name, &quot;Title of Article,&quot;{' '}
      <em>Name of Newspaper or Magazine</em>, Date of Publication, URL if
      accessed online.
    </CitationBlockMini>
    <p>
      <strong>Example:</strong>
    </p>
    <CitationExampleBox>
      Richard Dawkins, &quot;Life on Mars: A Reality?,&quot;{' '}
      <em>The Daily Martian</em>, April 30, 2065,
      https://www.dailyuniverse.com/life-on-mars.
    </CitationExampleBox>
    <p>
      Below you will see the standard structure for a bibliography entry for
      magazine and newspaper citations in the Chicago{' '}
      <TextWithColor>
        <TextWithColor>Notes and Bibliography</TextWithColor> style
      </TextWithColor>
      :
    </p>
    <CitationBlock header="Citing newspaper and magazine articles in Chicago">
      Author&apos;s Last Name, First Name. &quot;Title of Article.&quot;{' '}
      <em>Newspaper or Magazine Name</em>, Date of Publication. URL if accessed
      online.
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Dawkins, Richard. &quot;Life on Mars: A Reality?&quot;{' '}
      <em>The Daily Martian</em>, April 30, 2023.
      https://www.dailyuniverse.com/life-on-mars.
    </CitationExampleBox>
    <p>
      In the <TextWithColor>Author-Date style</TextWithColor>, sources are cited
      briefly in the text, usually in parentheses, by author&apos;s last name
      and date of publication. The short citations are amplified in a list of
      references, where full bibliographic information is provided.
    </p>
    <p>
      Here&apos;s the guide for citing newspaper and magazine articles in{' '}
      <TextWithColor>Author-Date</TextWithColor>:
    </p>
    <p>
      <strong>In-text citation for a newspaper or magazine article:</strong>
    </p>
    <CitationBlockMini>
      (Author&apos;s Last Name Year of Publication, page number if available)
    </CitationBlockMini>
    <p>
      <strong>Example:</strong>
    </p>
    <CitationExampleBox>(Bennett 2019, 14)</CitationExampleBox>
    <p>
      <strong>Reference list entry for a newspaper or magazine article:</strong>
    </p>
    <CitationBlockMini>
      Author&apos;s Last name, First name. Year. &quot;Title of Article.&quot;
      <em> Name of Newspaper or Magazine</em>, Month Day, Year. URL if accessed
      online.
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Bennett, Jessica. 2019. &quot;It&apos;s a New Morning for Jennifer
      Aniston.&quot; <em>The New York Times, </em> September 10, 2019.
      https://www.nytimes.com/2019/09/10/arts/television/jennifer-aniston-apple-morning-show.html.
    </CitationExampleBox>
    <p>
      <strong>Exceptions and notes:</strong>
    </p>
    <ul>
      <li>
        For newspaper articles, you typically do not need to include the
        specific page numbers in your citation.
      </li>
      <li>
        If an article is unsigned, begin the citation with the title of the
        article.
      </li>
      <li>
        If the article is from a wire service (e.g., Associated Press), use the
        service as the author in your citation.
      </li>
      <li>
        When citing an online article, if a DOI is available, use it in place of
        a URL.
      </li>
      <li>
        If the article is printed, and you don&apos;t have the exact date of
        publication, but only the year or month, adjust the citation
        accordingly.
      </li>
      <li>
        If the article is from a daily newspaper or magazine, include the exact
        date of publication. If from a weekly or monthly publication, just the
        month and year are needed.
      </li>
      <li>
        If an online article has no publication date, use the access date in its
        place.
      </li>
    </ul>

    <p>
      Remember, the goal of citation is to provide enough information for
      readers to find the source material themselves. If you&apos;re ever in
      doubt, it&apos;s better to provide a bit too much information than too
      little.
    </p>
  </GuideSection>
);

const CiteConferencePaper: React.FC = () => (
  <GuideSection id="CiteConferencePaper">
    <GuideHeader>Cite Conference Papers in Chicago Style</GuideHeader>
    <p>
      Conference papers can be a bit tricky because they&apos;re often published
      in various forms — a print collection of a conference&apos;s proceedings,
      a journal issue dedicated to the conference, or even not at all.
    </p>
    <p>
      Regardless, below you will find a detailed guide on how to cite conference
      papers in both the Notes-Bibliography style and the Author-Date style
      according to the Chicago Manual of Style.
    </p>

    <p>
      Here&apos;s how to structure conference paper citations in{' '}
      <TextWithColor>Notes and Bibliography</TextWithColor>:
    </p>
    <p>
      <strong>First note citation for a conference paper:</strong>
    </p>
    <CitationBlockMini>
      Author&apos;s First Name Last Name, &quot;Title of Paper&quot; (paper
      presented at Title of Conference, Institution, Location, Date of
      Conference), URL (if available).
    </CitationBlockMini>
    <p>
      <strong>Example:</strong>
    </p>
    <CitationExampleBox>
      Anna Kay, &quot;The Future of Tech: Silicon Valley Case Study&quot; (paper
      presented at the Annual Conference on Technology, UCLA, Los Angeles, CA,
      May 1, 2023).
    </CitationExampleBox>
    <p>
      Below you will see the standard structure for a bibliography entry for a
      conference paper citation in the Chicago Notes and Bibliography style:
    </p>
    <CitationBlock header="Citing a conference paper in Chicago">
      Author&apos;s Last Name, First Name. &quot;Title of Paper.&quot; Paper
      presented at Title of Conference, Institution, Location, Date of
      Conference. URL (if available).
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Kay, Anna. &quot;The Future of Tech: Silicon Valley Case Study.&quot;
      Paper presented at the Annual Conference on Technology, UCLA, Los Angeles,
      CA, May 1, 2023.
    </CitationExampleBox>
    <p>
      In the <TextWithColor>Author-Date style</TextWithColor>, sources are cited
      briefly in the text, usually in parentheses, by author&apos;s last name
      and date of publication. The short citations are amplified in a list of
      references, where full bibliographic information is provided.
    </p>
    <p>
      Here&apos;s the guide for citing conference papers in the{' '}
      <TextWithColor>Author-Date</TextWithColor> style:
    </p>
    <p>
      <strong>In-text citation for a conference paper:</strong>
    </p>
    <CitationBlockMini>
      (Author&apos;s Last Name Year of Publication, page number if available)
    </CitationBlockMini>
    <p>
      <strong>Example:</strong>
    </p>
    <CitationExampleBox>(Kay 2023)</CitationExampleBox>
    <p>
      <strong>Reference list entry for a conference paper:</strong>
    </p>
    <CitationBlockMini>
      Author&apos;s Last Name, First Name. Year. &quot;Title of Paper.&quot;
      Paper presented at Title of Conference, Institution, Location, Date of
      Conference. URL (if available).
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Kay, Anna. 2023. &quot;The Future of Technology.&quot; Paper presented at
      the Annual Conference on Technology, UCLA, Los Angeles, CA, May 1, 2023.
    </CitationExampleBox>
    <p>
      <strong>Exceptions and notes: </strong>
    </p>
    <ul>
      <li>
        If the conference paper is published in a journal or in conference
        proceedings, it should be cited according to the rules of that specific
        source type.
      </li>
      <li>
        If you accessed the conference paper online, include the URL at the end
        of your citation.
      </li>
      <li>
        If the conference paper is not formally published, the title should be
        capitalized sentence-style and not in italics or quotation marks.
      </li>
      <li>
        If the conference name includes significant information about the
        conference&apos;s number, location or date, or the sponsoring
        institution, include it as part of the title in the citation.
      </li>
    </ul>
    <p>
      Remember, the aim of citation is to provide enough information for your
      readers to be able to find the source material themselves. When in doubt,
      it&apos;s better to err on the side of providing more information.
    </p>
  </GuideSection>
);

const CiteAudioVisual: React.FC = () => (
  <GuideSection id="CiteAudioVisual">
    <GuideHeader>Cite Films in Chicago Style</GuideHeader>
    <p>
      In academic and professional writing, sources of information and
      inspiration are often not limited to traditional print materials. Audio
      and visual sources, such as films, television episodes, podcasts, and
      music tracks, often provide rich insights and perspectives.
    </p>
    <p>
      Citing these non-textual sources, however, can be a bit tricky. This guide
      aims to demystify the process by providing clear guidelines for citing
      audio and visual materials in both the Notes-Bibliography and Author-Date
      styles of the Chicago Manual of Style.
    </p>
    <p>
      Here&apos;s how to structure audio and visual media citations in{' '}
      <TextWithColor>Notes and Bibliography</TextWithColor>:
    </p>
    <p>
      <strong>Footnote for a film citation:</strong>
    </p>
    <CitationBlockMini>
      <em>Title of film</em>, directed by First Name Last Name (Production
      company or Distributor, Year), Timestamp(s) if relevant, URL or Format.
    </CitationBlockMini>
    <p>
      <strong>Example:</strong>
    </p>
    <CitationExampleBox>
      <em>The Lion King</em>, directed by Roger Allers and Rob Minkoff (Disney,
      2003), 1:25:13, https://movies.disney.com/the-lion-king-2019.
    </CitationExampleBox>
    <p>
      Below you will see the standard structure for a bibliography entry for
      film sources in the Chicago{' '}
      <TextWithColor>Notes and Bibliography</TextWithColor> style:
    </p>
    <CitationBlock header="Citing a film in Chicago">
      Director&apos;s Last Name, First Name, director. <em>Movie Title.</em>{' '}
      Production Company or Distributor, Year. Film length. URL.
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Favreau, Jon, director. <em>The Lion King. </em>
      Disney, 2019. 1 hr., 58 min. https://movies.disney.com/the-lion-king-2019.
    </CitationExampleBox>
    <p>
      In the <TextWithColor>Author-Date style</TextWithColor>, sources are cited
      briefly in the text, usually in parentheses, by author&apos;s last name
      and date of publication. The short citations are amplified in a list of
      references, where full bibliographic information is provided.
    </p>
    <p>
      Here&apos;s the guide for citing audio and video sources in the
      <TextWithColor> Author-Date style</TextWithColor>:
    </p>
    <p>
      <strong>In-text citation for a film:</strong>
    </p>
    <CitationBlockMini>
      (Director&apos;s Last Name Year, Timestamp)
    </CitationBlockMini>
    <p>
      <strong>Example:</strong>
    </p>
    <CitationExampleBox>(Favreau 2019, 1:25:13)</CitationExampleBox>
    <p>
      <strong>Reference list entry for a film:</strong>
    </p>
    <CitationBlockMini>
      Director&apos;s Last Name, First Name, director. Year.{' '}
      <em>Movie Title.</em> Production Company or Distributor. Film length. URL.
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Favreau, Jon, director. 2019. <em>The Lion King. </em>
      Disney. 1 hr., 58 min. https://movies.disney.com/the-lion-king-2019.
    </CitationExampleBox>
    <br />
    <p>
      If the audio or visual material is <strong>part of a larger work</strong>{' '}
      (like a television episode in a series), the title of the larger work
      should be included in the citation.
    </p>
    <p>
      Let&apos;s take an episode from the TV series &quot;Stranger Things.&quot;
      Here&apos;s how you&apos;d cite it:
    </p>
    <p>
      <strong>Chicago full note citation for a TV show episode:</strong>
    </p>
    <CitationExampleBox>
      <TextWithColor>
        <em>Stranger Things</em>, season 3, episode 8, &quot;Chapter Eight: The
        Battle of Starcourt,&quot;{' '}
      </TextWithColor>
      directed by Matt Duffer and Ross Duffer, aired July 4, 2019,
      https://www.netflix.com/watch/9825794, 00:10:33.
    </CitationExampleBox>

    <p>
      <strong>Chicago bibliography entry for a TV show episode:</strong>
    </p>
    <CitationExampleBox>
      Duffer, Matt, director.{' '}
      <TextWithColor>
        <em>Stranger Things.</em> Season 3, episode 8, &quot;Chapter Eight: The
        Battle of Starcourt.&quot;
      </TextWithColor>{' '}
      Aired July 4, 2019. https://www.netflix.com/watch/9825794.
    </CitationExampleBox>

    <p>
      <strong>Exceptions and notes:</strong>
    </p>
    <ul>
      <li>
        For online audio or video files, include the URL at the end of your
        citation. If a DOI is available, use it in place of a URL.
      </li>

      <li>
        If there are significant contributors besides the director or performer
        (like producers or screenplay writers), they can be included after the
        title.
      </li>
    </ul>
    <p>
      As with other citation types, the goal is to provide enough information
      for your readers to be able to locate the source material themselves. When
      in doubt, include more information rather than less.
    </p>
  </GuideSection>
);

const CiteSocialMedia: React.FC = () => (
  <GuideSection id="CiteSocialMedia">
    <GuideHeader>Cite Social Media Posts in Chicago Style</GuideHeader>
    <p>
      In today&apos;s digital age, social media platforms have become crucial
      sources of information, ideas, and discourse. From Twitter threads
      exploring the latest scientific discoveries to Instagram posts showcasing
      new artistic trends, these platforms are shaping the way we learn,
      communicate, and create. Now it&apos;s common to cite social media posts
      in academic and professional writing.
    </p>
    <p>
      However, citing such sources can be challenging, given their unique
      structure and the often informal nature of the content. This guide aims to
      simplify the process by providing clear guidelines for citing social media
      posts in both the Notes-Bibliography and Author-Date styles of the Chicago
      Manual of Style.
    </p>

    <p>
      When referencing brief social media content like tweets within your paper,
      it&apos;s enough to merely provide an in-text citation or footnote.
      However, if you&apos;re performing a detailed analysis or extensive
      discussion about such tweets, it would be appropriate to include them in
      the bibliography or references section.
    </p>
    <p>
      Here&apos;s how to structure citations for social media posts in{' '}
      <TextWithColor>Notes and Bibliography</TextWithColor>:
    </p>
    <p>
      <strong>Footnote for a social media post:</strong>
    </p>
    <CitationBlockMini>
      Author&apos;s First Name Last Name (@username if relevant), “Post text
      (160 characters max),” Platform, Month Day, Year, Time. URL.
    </CitationBlockMini>
    <p>
      <strong>Footnote citation for a Twitter post:</strong>
    </p>
    <CitationExampleBox>
      Taylor Swift (@taylorswift), &quot;Exciting developments in my career!
      #newrelease,&quot; Twitter, May 1, 2023, 5:22pm,
      https://twitter.com/taylorswift/status/54p23.
    </CitationExampleBox>
    <p>
      Below you will see the standard structure for a bibliography entry for
      social media posts in the Chicago{' '}
      <TextWithColor>Notes and Bibliography</TextWithColor> style:
    </p>
    <CitationBlock header="Citing social media posts in Chicago">
      Author&apos;s Last Name, First Name (@username), “Post text (max 160
      characters).” Platform, Month Day, Year, Timestamp. URL.
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Swift, Taylor (@taylorswift). &quot;I just broke up with my boyfriend!
      Already writing a new song...&quot; Twitter, May 1, 2023, 5:22pm.
      https://twitter.com/taylorswift/status/54p23.
    </CitationExampleBox>
    <p>
      In the <TextWithColor>Author-Date style</TextWithColor>, sources are cited
      briefly in the text, usually in parentheses, by author&apos;s last name
      and date of publication. The short citations are amplified in a list of
      references, where full bibliographic information is provided.
    </p>
    <p>
      Here&apos;s the guide for citing social media posts in the{' '}
      <TextWithColor>Author-Date</TextWithColor> style:
    </p>
    <p>
      <strong>In-text citation for a social media post:</strong>
    </p>
    <CitationBlockMini>
      (Author&apos;s Last Name Year of Publication)
    </CitationBlockMini>
    <p>
      <strong>Example:</strong>
    </p>
    <CitationExampleBox>(Swift 2023)</CitationExampleBox>
    <p>
      <strong>Reference list entry for a social media post:</strong>
    </p>
    <CitationBlockMini>
      Swift, Taylor (@taylorswift). 2023. &quot;Look at my new cat FiFi! So
      cute!&quot; Instagram, May 1, 2023.
      https://www.instagram.com/p/Clh4c75guDu/.
    </CitationBlockMini>
    <p>
      <strong>Exceptions and notes:</strong>
    </p>
    <ul>
      <li>
        If the post is long, it is acceptable to use a shortened form of the
        post in the citation, followed by an ellipsis (...).
      </li>
      <li>
        If the author&apos;s real name is not available, the username alone is
        sufficient.
      </li>
      <li>
        When citing a post with a hashtag, include the hashtag in the citation.
      </li>
      <li>
        Include the URL of the specific post when available, not just the URL of
        the platform or the user&apos;s profile.
      </li>
    </ul>
    <p>
      As always, the main goal of citation is to provide enough information for
      readers to find the source material themselves. If you&apos;re ever in
      doubt, it&apos;s better to include more information rather than less.
    </p>
  </GuideSection>
);

const CiteImagesArtwork: React.FC = () => (
  <GuideSection id="CiteImagesArtwork">
    <GuideHeader>Cite Images & Artwork in Chicago Style</GuideHeader>
    <p>
      Artwork and images often serve as powerful tools of communication,
      offering perspectives and insights that words alone may not fully capture.
      Whether it&apos;s a historical photograph, an avant-garde painting, or a
      poignant digital illustration, these visual forms of expression are
      increasingly being incorporated into academic and professional writing.
    </p>
    <p>
      Citing these sources correctly, however, can be somewhat complex due to
      factors such as the medium, dimensions, location, and availability of the
      work. This guide aims to simplify this process, providing comprehensive
      instructions for citing images and artwork in both the Notes-Bibliography
      and Author-Date styles of the Chicago Manual of Style.
    </p>
    <p>
      Here&apos;s how to structure citations for images and artwork in{' '}
      <TextWithColor>Notes and Bibliography</TextWithColor>:
    </p>
    <p>
      <strong>Footnote for an image:</strong>
    </p>
    <CitationBlockMini>
      Author&apos;s First Name Last Name, <em>Image Title</em>, Format, Website
      Name, Month Day, Year, URL.
    </CitationBlockMini>
    <p>
      <strong>Example:</strong>
    </p>
    <CitationExampleBox>
      Vincent van Gogh, <em>The Starry Night</em>, 1889, oil on canvas, Museum
      of Modern Art, www.moma.org/collection/works/79802.
    </CitationExampleBox>
    <p>
      Below you will see the standard structure for a bibliography entry for
      images and artworks in the Chicago{' '}
      <TextWithColor>Notes and Bibliography</TextWithColor> style:
    </p>
    <CitationBlock header="Citing images and artwork in Chicago">
      Author&apos;s Last Name, First Name. <em>Image Title.</em> Format. Website
      Name. Month Day, Year. URL.
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      van Gogh, Vincent. <em>The Starry Night.</em> Oil on canvas. Museum of
      Modern Art. 1889. www.moma.org/collection/works/79802.
    </CitationExampleBox>
    <p>
      In the <TextWithColor>Author-Date style</TextWithColor>, sources are cited
      briefly in the text, usually in parentheses, by author&apos;s last name
      and date of publication. The short citations are amplified in a list of
      references, where full bibliographic information is provided.
    </p>
    <p>
      Here&apos;s the guide for citing images and artwork in the{' '}
      <TextWithColor>Author-Date</TextWithColor> style:
    </p>
    <p>
      <strong>In-text citation for an image:</strong>
    </p>
    <CitationBlockMini>
      (Author&apos;s Last Name Year of Publication)
    </CitationBlockMini>
    <p>
      <strong>Example:</strong>
    </p>
    <CitationExampleBox>(van Gogh 1889)</CitationExampleBox>
    <p>
      <strong>Reference list entry for an image:</strong>
    </p>
    <CitationBlockMini>
      Author&apos;s Last Name, First Name. Year. <em>Image Title.</em> Format.
      Website Name. Month Day, Year. URL.
    </CitationBlockMini>
    <p>
      <strong>For example</strong>:
    </p>
    <CitationExampleBox>
      van Gogh, Vincent. 1889. <em>The Starry Night.</em> Oil on canvas. Museum
      of Modern Art. Accessed May 1, 2017. www.moma.org/collection/works/79802.
    </CitationExampleBox>
    <p>
      <strong>Exceptions and notes</strong>:
    </p>
    <ul>
      <li>If the artwork is viewed in person, omit the URL.</li>
      <li>
        If the artwork is part of a larger publication and not viewed directly,
        cite it as you would any part of a book.
      </li>
      <li>
        If there is no title, provide a brief description of the work instead of
        a title. Here&apos;s an example of how this would look like in a
        footnote citation:
        <CitationExampleInList>
          Unknown artist,{' '}
          <TextWithColor>
            Untitled (portrait of a woman in a red dress)
          </TextWithColor>
          , 1805, oil on canvas, Private Collection.
        </CitationExampleInList>
      </li>
      <li>
        The dimensions of the artwork do not need to be included, but it can be
        helpful in some disciplines.
      </li>
      <li>
        If the year of creation is unknown, use &quot;n.d.&quot; for &quot;no
        date.&quot;
      </li>
    </ul>
  </GuideSection>
);

const CiteThesesDissertations: React.FC = () => (
  <GuideSection id="CiteThesesDissertations">
    <GuideHeader>Cite Theses & Dissertations in Chicago Style</GuideHeader>
    <p>
      Theses and dissertations represent the culmination of a significant body
      of work, often serving as an essential resource in their respective
      fields. Whether you are citing a master&apos;s thesis in history, a
      doctoral dissertation in psychology, or a similar work in any other
      discipline, it&apos;s important to credit this rigorous scholarly effort
      accurately. This guide provides detailed instructions for citing theses
      and dissertations in both the Notes-Bibliography and Author-Date Chicago
      Styles.
    </p>
    <p>
      Below is the basic citation structure for theses and dissertations in the{' '}
      <TextWithColor>Notes and Bibliography</TextWithColor> format:
    </p>
    <p>
      <strong>Footnote citation for a thesis or dissertation:</strong>
    </p>
    <CitationBlockMini>
      Author&apos;s First Name Last Name, &quot;Title of Thesis&quot; Type of
      Thesis., (Name of Institution, Year), URL or Database (if applicable).
    </CitationBlockMini>
    <p>
      <strong>Example:</strong>
    </p>
    <CitationExampleBox>
      Michael Morningstar, &quot;Social Engineering and Revolt Planning&quot;
      PhD diss., (Massachusetts Institute of Technology, 2003), ProQuest
      Dissertations.
    </CitationExampleBox>
    <p>
      Below you will see the standard structure for a bibliography entry for
      theses and dissertations in the Chicago{' '}
      <TextWithColor>Notes and Bibliography</TextWithColor> style:
    </p>
    <CitationBlock header="Citing theses and dissertations in Chicago">
      Author&apos;s Last Name, First Name. &quot;Title of Thesis.&quot; Type of
      Thesis. Name of Institution, Year. URL or Database Name (if applicable).
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Morningstar, Lucifer. &quot;The Role of Emotion in Human
      Development.&quot; PhD diss. Massachusetts Institute of Technology, 2003.
      ProQuest Dissertations.
    </CitationExampleBox>
    <p>
      In the <TextWithColor>Author-Date style</TextWithColor>, sources are cited
      briefly in the text, usually in parentheses, by author&apos;s last name
      and date of publication. The short citations are amplified in a list of
      references, where full bibliographic information is provided.
    </p>
    <p>
      Here&apos;s the guide for citing theses and dissertations in the{' '}
      <TextWithColor>Author-Date</TextWithColor> style:
    </p>
    <p>
      <strong>In-text citation for a thesis or dissertation:</strong>
    </p>
    <CitationBlockMini>
      (Author&apos;s Last Name Year of Publication, page numbers)
    </CitationBlockMini>
    <p>
      <strong>Example:</strong>
    </p>
    <CitationExampleBox>(Morningstar 2003, 99&ndash;100)</CitationExampleBox>
    <p>
      <strong>Reference list entry for a thesis or dissertation:</strong>
    </p>
    <CitationBlockMini>
      Author&apos;s Last Name, First Name. Year. &quot;Title of Thesis.&quot;
      Type of Thesis., Name of Institution.
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Decker, Chloe. 2023. &quot;Benefits of Partnership in Law
      Enforcement.&quot; PhD diss., UCLA.
    </CitationExampleBox>
    <p>
      If the thesis or dissertation was found in a public database, include the
      URL of the thesis. If the thesis or dissertation is not publicly
      accessible, or was read in print, omit the URL.
    </p>
    <p>
      As with other citation types, the main goal is to provide enough
      information for your readers to be able to locate the source material
      themselves. When in doubt, include more information rather than less.
    </p>
  </GuideSection>
);

const CiteLegalDocuments: React.FC = () => (
  <GuideSection id="CiteLegalDocuments">
    <GuideHeader>Cite Legal Documents in Chicago Style</GuideHeader>
    <p>
      Government and state documents serve as vital resources in a variety of
      academic fields, offering authoritative data, policy analysis, legislative
      history, and more. Given their diversity and the specific manner in which
      they are organized and referenced, citing these materials appropriately
      can be complex. This guide provides detailed instructions on citing legal
      and state documents in both the Notes-Bibliography and Author-Date styles
      of the Chicago Manual of Style.
    </p>
    <p>
      Below is the basic citation structure for government documents in the{' '}
      <TextWithColor>Notes and Bibliography</TextWithColor> format:
    </p>
    <p>
      <strong>Footnote citation for a government document:</strong>
    </p>
    <CitationBlockMini>
      Name of Government Department, Agency or Committee,{' '}
      <em>Title of Publication</em>, Publication Date, page number (if
      available), URL.
    </CitationBlockMini>
    <p>
      <strong>Example:</strong>
    </p>
    <CitationExampleBox>
      Competition Bureau of Canada, <em>Emerging Competition Issues</em>, March
      4, 2016, 3, www.competitionbureau.gc.ca/eic/site/Workshop-Summary-Report.
    </CitationExampleBox>
    <p>
      Below you will see the standard structure for a bibliography entry for
      government and state documents in the Chicago{' '}
      <TextWithColor>Notes and Bibliography</TextWithColor> style:
    </p>
    <CitationBlock header="Citing government documents in Chicago">
      Name of Government Department, Agency or Committee.{' '}
      <em>Title of Publication.</em> Publication Date. URL.
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Competition Bureau of Canada. <em>Emerging Competition Issues</em>. March
      4, 2016. www.competitionbureau.gc.ca/eic/site/Workshop-Summary-Report.
    </CitationExampleBox>
    <br />
    <p>
      For specific Federal or State Statutes or US Supreme Court Cases, the
      citations will follow a slightly different format, which you can see
      below.
    </p>
    <p>
      <strong>Footnote structure for a Federal or State Statute:</strong>
    </p>
    <CitationBlockMini>Name of Act, Public Law No. (Year).</CitationBlockMini>
    <p>
      <strong>Example:</strong>
    </p>
    <CitationExampleBox>
      The Patient Protection and Affordable Care Act, Publ. L. No.
      111&ndash;148, 124 Stat. 119 (2010).
    </CitationExampleBox>
    <p>
      <strong>Footnote structure for a US Supreme Court Case:</strong>
    </p>
    <CitationBlockMini>
      Name v. Name, Volume # U.S. Page # (Year).
    </CitationBlockMini>
    <p>
      <strong>Example:</strong>
    </p>
    <CitationExampleBox>
      Tinker v. Des Moines Independent Community School Dist., 393 U.S. 503
      (1969).
    </CitationExampleBox>

    <p>
      The Chicago Manual of Style doesn&apos;t provide many specific examples
      for citing legal materials in the{' '}
      <TextWithColor>Author-Date</TextWithColor> style, as this style is more
      common in physical, natural, and social sciences. However, it recommends
      referring to{' '}
      <a href="https://www.legalbluebook.com/">
        The Bluebook: A Uniform System of Citation
      </a>
      .
    </p>
    <p>
      <strong>Exceptions and notes:</strong>
    </p>
    <ul>
      <li>
        For cases or laws that have a well-known popular name, you may include
        that in your citation.
      </li>
      <li>
        If the document is available online, include the URL and the date of
        access.
      </li>
      <li>
        If you&apos;re citing the U.S. Constitution, abbreviate it as &quot;U.S.
        Const.&quot;
      </li>
    </ul>
  </GuideSection>
);

const CiteClassicalWorks: React.FC = () => (
  <GuideSection id="CiteClassicalWorks">
    <GuideHeader>Cite Classical Works in Chicago Style</GuideHeader>

    <p>
      Citations of ancient texts are typically confined to footnotes or
      endnotes, and only included in a bibliography when referencing commentary
      or annotations from a modern editor. The unique identifiers that mark
      distinct parts of classical pieces, including books, sections, and lines,
      remain consistent across all versions, whether they are in the original
      language or translated.
    </p>
    <p>
      <strong>Example:</strong>
    </p>
    <CitationExampleBox>
      Plato, <em>Republic</em> 360e&ndash;361b
    </CitationExampleBox>
    <p>
      Below is the basic structure for citing classical works in{' '}
      <TextWithColor>Notes and Bibliography</TextWithColor>:
    </p>
    <p>
      <strong>
        Footnote for a classical work translated and edited by a modern author:
      </strong>
    </p>
    <CitationBlockMini>
      Author&apos;s Name, <em>Title of Work,</em> ed. Editor&apos;s Name and/or
      trans. Translator&apos;s Name (if applicable), (Location: Publisher,
      Year), Book/Section/Line.
    </CitationBlockMini>
    <p>
      <strong>Example:</strong>
    </p>
    <CitationExampleBox>
      Propertius, <em>Elegies</em>, ed. and trans. G. P. Goold, (Cambridge, MA:
      Harvard University Press, 1990), 45.
    </CitationExampleBox>
    <p>
      Below you will see the standard structure for a bibliography entry for
      classical works in the Chicago{' '}
      <TextWithColor>Notes and Bibliography</TextWithColor> style:
    </p>
    <CitationBlock header="Citing classical works in Chicago">
      Author&apos;s Name. <em>Title of Work.</em> Edited by Editor&apos;s Name
      and/or Translated by Translator&apos;s Name (if applicable). Location:
      Publisher, Year.
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Propertius. <em>Elegies.</em> Edited and translated by G. P. Goold.
      Cambridge, MA: Harvard University Press, 1990.
    </CitationExampleBox>
    <p>
      In the <TextWithColor>Author-Date style</TextWithColor>, sources are cited
      briefly in the text, usually in parentheses, by author&apos;s last name
      and date of publication. The short citations are amplified in a list of
      references, where full bibliographic information is provided.
    </p>
    <p>
      Here&apos;s the guide for citing classical works in the{' '}
      <TextWithColor>Author-Date</TextWithColor> style:
    </p>
    <p>
      <strong>In-text citation for a classical work:</strong>
    </p>
    <CitationBlockMini>
      (Author&apos;s Last Name Year, Book/Section/Line)
    </CitationBlockMini>
    <p>
      <strong>Example:</strong>
    </p>
    <CitationExampleBox>(Homer 1996, 11.605-10)</CitationExampleBox>
    <p>
      <strong>Reference list entry for a classical work:</strong>
    </p>
    <CitationBlockMini>
      Author&apos;s Last Name, First Name. Year. <em>Title of Work.</em>{' '}
      Translated by Translator&apos;s Name (if applicable). Location: Publisher.
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Homer. 1996. <em>The Odyssey.</em> Translated by Robert Fagles. New York:
      Viking.
    </CitationExampleBox>
    <p>
      <strong>Exceptions and notes:</strong>
    </p>
    <ul>
      <li>
        If citing from an anthology or collection, include the editor&apos;s
        name in the citation.
      </li>
      <li>
        When citing ancient texts, it is common to cite by book/section/line
        numbers rather than page numbers, since they will be the same across
        different editions.
      </li>
      <li>
        For ancient works, the year of the translation or version you used is
        what should be included in the citation, not the original publication
        year.
      </li>
    </ul>
  </GuideSection>
);

const CiteCourseMaterials: React.FC = () => (
  <GuideSection id="CiteCourseMaterials">
    <GuideHeader>Cite Course Materials & Lectures in Chicago</GuideHeader>
    <p>
      Course materials and lectures, whether they are from your own classes or
      an online course, are vital sources of information. These can include
      lecture notes, slides, study guides, or other instructional materials
      provided by professors or instructors. This guide will outline how to cite
      course materials and lectures using both the Notes-Bibliography and
      Author-Date styles of the Chicago Manual of Style.
    </p>
    <p>
      Below is the basic structure for citing course materials in{' '}
      <TextWithColor>Notes and Bibliography</TextWithColor>:
    </p>
    <p>
      <strong>Footnote for a class lecture:</strong>
    </p>
    <CitationBlockMini>
      Author&apos;s Name, &quot;Title of Lecture or Material,&quot; Course Name
      (class lecture, Institution, Location, Date of Lecture or Material).
    </CitationBlockMini>
    <p>
      <strong>Example:</strong>
    </p>
    <CitationExampleBox>
      Allen Seager, &quot;Women and the Church in New France,&quot; History 204:
      The Social History of Canada (class lecture, Simon Fraser University,
      Burnaby, BC, January 13, 2011).
    </CitationExampleBox>
    <p>
      Below you will see the standard structure for a bibliography entry for
      course materials and lectures in the Chicago{' '}
      <TextWithColor>Notes and Bibliography</TextWithColor> style:
    </p>
    <CitationBlock header="Citing course materials in Chicago">
      Autho&apos; Last Name, First Name. &quot;Title of Material.&quot; Course
      Name. Class lecture at Institution, Location, Date of Lecture or Material.
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Seager, Allen. &quot;Women and the Church in New France.&quot; History
      204: The Social History of Canada. Class lecture at Simon Fraser
      University, Burnaby, BC, January 13, 2011.
    </CitationExampleBox>
    <p>
      In the <TextWithColor>Author-Date style</TextWithColor>, sources are cited
      briefly in the text, usually in parentheses, by authors last name and date
      of publication. The short citations are amplified in a list of references,
      where full bibliographic information is provided.
    </p>
    <p>
      Heres the guide for citing classical works in the{' '}
      <TextWithColor>Author-Date</TextWithColor> style:
    </p>
    <p>
      <strong>In-text citation for a course material:</strong>
    </p>
    <CitationBlockMini>(Author&apos;s Last Name Year)</CitationBlockMini>
    <p>
      <strong>Example:</strong>
    </p>
    <CitationExampleBox>(Seager 2011)</CitationExampleBox>
    <p>
      <strong>Reference list entry for a course material:</strong>
    </p>
    <CitationBlockMini>
      Author&apos;s Last Name, First Name. Year. &quot;Title of Lecture or
      Material.&quot; Lecture, Course Name, Institution, Location, Date of
      Lecture or Material.
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Smith, John. 2023. &quot;Understanding Quantum Mechanics.&quot; Lecture,
      PHYS 101, University of Chicago, Chicago, IL, January 2, 2023.
    </CitationExampleBox>
    <p>
      <strong>Exceptions and notes:</strong>
    </p>
    <ul>
      <li>
        Course materials such as lecture notes/slides that are available online
        can also include a URL.
      </li>
      <li>
        If the material is not titled, describe it in square brackets after the
        author&apos;s name (e.g., [lecture notes]).
      </li>
      <li>
        For guest lectures, include the lecturer&apos;s name, not the course
        instructor&apos;s.
      </li>
    </ul>
  </GuideSection>
);

const CitePersonalComms: React.FC = () => (
  <GuideSection id="CitePersonalComms">
    <GuideHeader>Cite Personal Communications in Chicago</GuideHeader>
    <p>
      Personal communications encompass a wide range of materials including
      letters, emails, text messages, social media posts, and even private
      conversations. Unlike most published sources, these forms of communication
      often can&apos;t be retrieved by readers. Hence, they should be used
      sparingly in academic writing, and always with the explicit consent of the
      sender or speaker.
    </p>
    <p>
      This guide will outline how to cite personal communications using both the
      Notes-Bibliography and Author-Date styles in the Chicago Manual of Style.
    </p>
    <p>
      Below is the format for citing personal communications in{' '}
      <TextWithColor>Notes and Bibliography</TextWithColor>:
    </p>
    <p>
      <strong>Footnote for a personal communication:</strong>
    </p>
    <CitationBlockMini>
      Sender/Communicator&apos;s First and Last Name, Type of Communication,
      Month Day, Year.
    </CitationBlockMini>
    <p>
      <strong>Footnote example for an email:</strong>
    </p>
    <CitationExampleBox>
      Anne Hathaway, email message to author, January 2, 2023.
    </CitationExampleBox>
    <p>
      <strong>Example:</strong>
    </p>

    <p>
      <strong>Foornote example for an interview:</strong>
    </p>
    <CitationExampleBox>
      Anne Hathaway, interview by Mike Mayor, March 12, 2023.
    </CitationExampleBox>
    <p>
      The <TextWithColor>Notes and Bibliography</TextWithColor> style uses
      footnotes or endnotes in the text, but personal communications are
      typically not included in the bibliography because they cannot be
      retrieved by readers.
    </p>
    <p>
      In the <TextWithColor>Author-Date</TextWithColor> style, personal
      communications are cited briefly in the text or in parentheses, but not
      included in the reference list as it is non-recoverable data.
    </p>
    <p>
      <strong>In-text citation for a personal communication:</strong>
    </p>
    <CitationBlockMini>
      (Sender&apos;s Last Name, type of communication, Month Day, Year)
    </CitationBlockMini>
    <p>
      <strong>Example:</strong>
    </p>
    <CitationExampleBox>
      (Anne Hathaway, email to author, December 18, 2019.)
    </CitationExampleBox>
    <p>
      <strong>Exceptions and notes:</strong>
    </p>
    <ul>
      <li>
        Personal communications, including email and text messages, are usually
        cited in the text or in a note only; they&apos;re rarely included in a
        bibliography or reference list.
      </li>
      <li>
        It is crucial to obtain permission from the person involved before
        citing personal communication.
      </li>
      <li>
        If a recorded, publicly available interview was conducted with the
        person, this should be cited as a separate type of source rather than
        personal communication.
      </li>
    </ul>
    <p>
      Remember, while personal communications can sometimes provide valuable
      insights or context, they should be used sparingly in academic writing due
      to their inherently unverifiable and non-recoverable nature. Always strive
      to use the most authoritative and accessible sources possible in your
      work.
    </p>
  </GuideSection>
);

const FinalNotes: React.FC = () => (
  <GuideSection id="FinalNotes">
    <GuideHeader>Chicago Style: Final Notes</GuideHeader>
    <p>
      The Chicago citation format offers a consistent and comprehensive method
      for citing a variety of sources in your academic work. By becoming adept
      at the Chicago citation style, you enhance the professionalism of your
      work and aid your readers in easily tracking down the sources you&apos;ve
      referenced.
    </p>
    <p>
      This detailed guide on the Chicago citation style is intended to provide
      you with a strong basis for accurately citing sources in your research.
      Always remember that <Link href="/">Bibcitation</Link> is always here to
      assist you in your academic journey, offering a precise and user-friendly
      citation generator for your writing.
    </p>
  </GuideSection>
);

export default ChicagoGuide;
